import { useCallback, useMemo } from 'react';
import DownFilled from '@distribusion/portal-icons/dist/DownFilled';
import { useSession } from '@distribusion/portal-lib/auth';
import { Text } from '@distribusion/portal-lib/components';
import { CurrencyEnum } from '@distribusion/portal-lib/constants';
import { useUserSettingsStore } from '@distribusion/portal-lib/user-settings';
import { getCurrencySymbol } from '@distribusion/portal-lib/utils';
import { Button, Dropdown, MenuProps, Space } from 'antd';
import { useUpdateUserPreferences } from 'features/users/api/users';
import { useCurrencyOptions } from 'utils/use-currency-options';
import { useMount } from 'utils/use-mount';

import './currency-switch.less';

export const CurrencySwitch = () => {
  const { mainCurrency, setMainCurrency, currencyOptions, setCurrencyOptions } =
    useUserSettingsStore();
  const { data: sessionData } = useSession();
  const updateLocalizationPreferences = useUpdateUserPreferences();
  const items = useCurrencyOptions(currencyOptions);

  const setDefaultCurrencies = useCallback(() => {
    const metadata = sessionData.identity?.metadata_public || {};
    if ('currency' in metadata && mainCurrency !== metadata.currency) {
      setMainCurrency(metadata.currency as CurrencyEnum);
    }
    if ('currency_options' in metadata && currencyOptions !== metadata.currency_options) {
      setCurrencyOptions(metadata.currency_options as CurrencyEnum[]);
    }
  }, [
    currencyOptions,
    mainCurrency,
    sessionData.identity?.metadata_public,
    setCurrencyOptions,
    setMainCurrency,
  ]);

  useMount(setDefaultCurrencies);

  const selectedItem = useMemo(
    () => items.find((item) => item.key === mainCurrency),
    [items, mainCurrency],
  );

  const onClick: MenuProps['onClick'] = useCallback(
    ({ key }) => {
      setMainCurrency(key as CurrencyEnum);
      updateLocalizationPreferences.mutate({
        id: sessionData.identity.id,
        currency: key as CurrencyEnum,
      });
    },
    [sessionData.identity.id, setMainCurrency, updateLocalizationPreferences],
  );

  return (
    <Dropdown
      menu={{ items, selectable: true, selectedKeys: [mainCurrency], onClick }}
      trigger={['click']}
      overlayClassName="currency-switch"
    >
      <Button type="text" size="large">
        <Space size="small">
          {selectedItem && (
            <Text strong>
              {getCurrencySymbol((selectedItem.key as string) || CurrencyEnum.EUR)}
            </Text>
          )}
          <DownFilled />
        </Space>
      </Button>
    </Dropdown>
  );
};
